import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import {
  LEGAL_DOCUMENT_TYPES,
  LegalAcceptance,
  LegalDocumentType,
  listMyLegalAcceptances,
} from "../../services/legalDocument.service";
import { formatVersionDisplay } from "../../utils/semver";
import { useAuth } from "../../hooks/useAuth";
import {
  ArrowLeft,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  FileText,
  History,
  LogOut,
  ScrollText,
} from "lucide-react";

const PAGE_SIZE = 10;

const labelFor = (type: LegalDocumentType): string =>
  LEGAL_DOCUMENT_TYPES.find((t) => t.value === type)?.label ?? type;

const formatDateTime = (iso?: string): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d
    .toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .replace(/\b(am|pm)\b/g, (m) => m.toUpperCase());
};

const ActionBadge = ({ action }: { action: LegalAcceptance["action"] }) =>
  action === "ACCEPTED" ? (
    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
      <CheckCircle2 size={12} />
      Accepted
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
      <ClipboardCheck size={12} />
      Acknowledged
    </span>
  );

const LegalAcceptanceHistory = () => {
  const { user, logout } = useAuth();

  const [acceptances, setAcceptances] = useState<LegalAcceptance[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (pageNum: number) => {
    setLoading(true);
    try {
      const res = await listMyLegalAcceptances({
        page: pageNum,
        limit: PAGE_SIZE,
      });
      setAcceptances(res.acceptances);
      setTotal(res.total);
      setTotalPages(res.totalPages || 1);
      setPage(res.page);
    } catch {
      toast.error("Could not load acceptance history");
      setAcceptances([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(1);
  }, [load]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Link
              to="/admin/legal"
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors shrink-0"
              aria-label="Back to legal documents"
            >
              <ArrowLeft size={20} />
            </Link>
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-9 w-9 rounded-lg bg-primary-50 flex items-center justify-center text-primary-600 shrink-0">
                <History size={18} />
              </div>
              <div className="min-w-0">
                <h1 className="text-base sm:text-lg font-bold text-gray-900 tracking-tight truncate">
                  Acceptance History
                </h1>
                <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider truncate">
                  {user?.role}
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={logout}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors duration-200 shrink-0"
          >
            <LogOut size={16} />
            <span>Sign Out</span>
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-primary-50 flex items-center justify-center text-primary-600 shrink-0">
              <ScrollText size={16} />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">
                Your legal document history
              </p>
              <p className="text-xs text-gray-400">
                {loading ? "Loading..." : `${total} record${total === 1 ? "" : "s"} · read-only`}
              </p>
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <p className="text-gray-500 animate-pulse">Loading history...</p>
            </div>
          ) : acceptances.length === 0 ? (
            <div className="p-12 text-center">
              <div className="h-14 w-14 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-4">
                <FileText size={22} className="text-gray-300" />
              </div>
              <p className="text-gray-500 text-base font-medium">
                No acceptance history yet
              </p>
              <p className="text-gray-400 text-sm mt-1">
                Documents you review and accept will appear here.
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-gray-50/80 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3.5 text-xs font-bold text-gray-700">
                        Document
                      </th>
                      <th className="px-6 py-3.5 text-xs font-bold text-gray-700">
                        Version
                      </th>
                      <th className="px-6 py-3.5 text-xs font-bold text-gray-700">
                        Action
                      </th>
                      <th className="px-6 py-3.5 text-xs font-bold text-gray-700 text-right">
                        Date / Time
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-sm">
                    {acceptances.map((a) => (
                      <tr key={a.id} className="hover:bg-gray-50/80 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="h-9 w-9 rounded-lg bg-gray-50 flex items-center justify-center text-gray-500 shrink-0">
                              <FileText size={16} />
                            </div>
                            <p className="font-medium text-gray-900">
                              {labelFor(a.documentType)}
                            </p>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold bg-neutral-100 text-neutral-700 border border-neutral-200">
                            {formatVersionDisplay(a.version, true)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <ActionBadge action={a.action} />
                        </td>
                        <td className="px-6 py-4 text-gray-600 whitespace-nowrap text-right">
                          {formatDateTime(a.acceptedAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
                  <p className="text-xs text-gray-400">
                    Page {page} of {totalPages}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => load(page - 1)}
                      disabled={page <= 1 || loading}
                      className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      aria-label="Previous page"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => load(page + 1)}
                      disabled={page >= totalPages || loading}
                      className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      aria-label="Next page"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <p className="text-center text-xs text-gray-400 mt-4">
          This history is view-only. Records cannot be edited or deleted.
        </p>
      </div>
    </div>
  );
};

export default LegalAcceptanceHistory;